/**
 * TanStack Query hooks for tenant branding (Phase B2.6).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AssetKind,
  BrandingPublic,
  CommitBrandingRequest,
  UploadUrlRequest,
  UploadUrlResponse,
} from "./types";

const SA_BASE = (slug: string) => `/super-admin/tenants/${slug}/branding`;
const PUBLIC_BASE = (slug: string) => `/tenants/${slug}/branding`;

export function useBranding(slug: string) {
  return useQuery<BrandingPublic>({
    queryKey: ["branding", slug],
    queryFn: () => api.get<BrandingPublic>(PUBLIC_BASE(slug)),
    enabled: Boolean(slug),
  });
}

export function useMintUploadUrl(slug: string, kind: AssetKind) {
  return useMutation<UploadUrlResponse, Error, UploadUrlRequest>({
    mutationFn: (body) => api.post<UploadUrlResponse>(`${SA_BASE(slug)}/${kind}/upload-url`, body),
  });
}

export function useCommitBranding(slug: string) {
  const qc = useQueryClient();
  return useMutation<BrandingPublic, Error, CommitBrandingRequest>({
    mutationFn: (body) => api.put<BrandingPublic>(SA_BASE(slug), body),
    onSuccess: (returned) => {
      // Phase H0 hotfix: write the BE response directly into the
      // cache instead of just invalidating. The previous
      // `invalidateQueries`-only path triggered a re-fetch that
      // raced the browser HTTP cache (5-minute TTL) and the
      // panel's "Unsaved" indicator stayed pinned for minutes.
      // setQueryData primes the cache from the authoritative
      // response so the dirty-tracker resets immediately, AND we
      // still invalidate so any other tab / re-mount picks up the
      // fresh bytes on its next focus.
      qc.setQueryData(["branding", slug], returned);
      qc.invalidateQueries({ queryKey: ["branding", slug] });
    },
  });
}

export function useDeleteBrandingAsset(slug: string) {
  const qc = useQueryClient();
  return useMutation<BrandingPublic | void, Error, AssetKind>({
    mutationFn: async (kind) => {
      await api.delete(`${SA_BASE(slug)}/${kind}`);
      // After delete the BE recomputes default branding; refetch to
      // hydrate setQueryData from the same authoritative source.
      return api.get<BrandingPublic>(PUBLIC_BASE(slug));
    },
    onSuccess: (refreshed) => {
      if (refreshed) {
        qc.setQueryData(["branding", slug], refreshed);
      }
      qc.invalidateQueries({ queryKey: ["branding", slug] });
    },
  });
}
